import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetUserMarketplaceTransactionsQuery } from '../../store/slices/apiSlice';
import Navbar from '../../components/common/Navbar';

interface Transaction {
  transaction_id: number;
  listing: {
    listing_id: number;
    title: string;
    listing_type: 'car' | 'house';
  } | null;
  amount: number;
  payment_method: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  wallet_amount: number;
  payment_screenshot: string | null;
  external_reference: string | null;
  created_at: string;
}

const MarketplaceTransactions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded'>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const { data: transactionsResponse, isLoading } = useGetUserMarketplaceTransactionsQuery({
    page: 1,
    limit: 50,
    status: statusFilter === 'all' ? undefined : statusFilter,
  }, {
    skip: !user,
  });

  const transactions = transactionsResponse?.data?.transactions || [];

  const formatCurrency = (amount: number, symbol: string | null = null) => {
    const currencySymbol = symbol || '$';
    return `${currencySymbol}${amount.toLocaleString('en-US')}`;
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="danger" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Failed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="danger" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Cancelled
          </Badge>
        );
      case 'refunded':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Refunded
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredTransactions = transactions;

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Transactions</h1>
            <p className="text-gray-600">View your marketplace payment transactions</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
            {(['all', 'pending', 'completed', 'failed', 'cancelled', 'refunded'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </Card>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <Card className="p-12 text-center">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't made any transactions yet."
                : `No ${statusFilter} transactions found.`}
            </p>
            <Button onClick={() => navigate('/marketplace')}>Browse Marketplace</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredTransactions.map((transaction) => (
              <Card key={transaction.transaction_id} className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {transaction.listing?.title || 'N/A'}
                        </h3>
                        {transaction.listing && (
                          <p className="text-sm text-gray-600">
                            {transaction.listing.listing_type === 'car' ? 'Car' : 'House'} Purchase
                          </p>
                        )}
                      </div>
                      {getStatusBadge(transaction.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Amount</label>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(transaction.amount)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Payment Method</label>
                        <p className="text-sm text-gray-900 capitalize">
                          {transaction.payment_method}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Date</label>
                        <p className="text-sm text-gray-900">
                          {formatDate(transaction.created_at)}
                        </p>
                      </div>
                      {transaction.payment_screenshot && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowImageModal(true);
                            }}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View Screenshot
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && selectedTransaction?.payment_screenshot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Payment Screenshot</h2>
                <button
                  onClick={() => {
                    setShowImageModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <img
                src={selectedTransaction.payment_screenshot}
                alt="Payment screenshot"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default MarketplaceTransactions;
